import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Target, Shield, Sparkles, Quote } from 'lucide-react';

export function AboutPage() {
  return (
    <div className="bg-white pt-20">
      <section className="py-24 bg-gradient-to-b from-[#FDF8F4] to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="inline-block text-coral-600 font-medium text-sm tracking-wide uppercase mb-4">
            Our Story
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6 leading-tight">
            We built what we
            <span className="block text-coral-500">wish we had</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            A private space to figure things out. No judgment, just clarity.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-6">
                The problem we saw
              </h2>
              <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                <p>
                  We kept hearing the same thing from friends: "I don't know what I want." They felt stuck, overwhelmed by options, or just... lost.
                </p>
                <p>
                  Traditional therapy was expensive and hard to access. Life coaches were hit or miss. And talking to friends about deep stuff? Awkward.
                </p>
                <p>
                  So we built VZMO. A private AI coach that's available whenever you need it. No appointments, no judgment, no awkward silences.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-coral-100/50 to-sage-100/50 rounded-3xl blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop"
                alt="People collaborating"
                className="relative rounded-3xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Quote className="h-12 w-12 text-coral-400 mx-auto mb-8" />
          <blockquote className="text-2xl md:text-3xl font-heading leading-relaxed mb-8">
            "The quality of your life depends on the quality of the questions you ask yourself."
          </blockquote>
          <p className="text-slate-400">The philosophy behind VZMO</p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              What we believe
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we build
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: 'Self-compassion first',
                description: 'Growth starts with accepting where you are. No shame, no judgment. Just honest reflection.',
                color: 'coral'
              },
              {
                icon: Users,
                title: 'Everyone deserves support',
                description: 'Quality coaching shouldn\'t be a luxury. We\'re making it accessible to everyone.',
                color: 'sage'
              },
              {
                icon: Target,
                title: 'Clarity over productivity',
                description: 'Before you optimize your life, figure out what you actually want. Direction before speed.',
                color: 'slate'
              },
              {
                icon: Shield,
                title: 'Privacy is sacred',
                description: 'Your deepest thoughts deserve protection. We don\'t sell data or train models on your entries.',
                color: 'coral'
              },
              {
                icon: Sparkles,
                title: 'Small steps, big changes',
                description: 'Transformation happens in moments, not marathons. Consistent small actions compound.',
                color: 'sage'
              },
              {
                icon: Quote,
                title: 'Questions over answers',
                description: 'The right question unlocks more than any advice. We help you think, not just react.',
                color: 'slate'
              }
            ].map((value, index) => {
              const colorClasses = {
                coral: 'bg-coral-100 text-coral-600',
                sage: 'bg-sage-100 text-sage-600',
                slate: 'bg-slate-100 text-slate-600'
              };
              return (
                <div key={index} className="bg-[#FDFBF7] rounded-2xl p-8">
                  <div className={`w-12 h-12 rounded-xl ${colorClasses[value.color as keyof typeof colorClasses]} flex items-center justify-center mb-5`}>
                    <value.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-slate-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-[#FDF8F4] to-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-6">
            A small team with a big mission
          </h2>
          <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
            <p>
              We're not a big tech company. We're a small team who genuinely cares about helping people find their way.
            </p>
            <p>
              We've been through the confusion ourselves. The career uncertainty. The relationship questions. The "what am I doing with my life?" moments.
            </p>
            <p>
              That's why we built VZMO to be different: private, personal, and actually helpful.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Ready to find your clarity?
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            Start your free trial. No credit card required.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-white text-slate-900 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all shadow-lg"
          >
            Start free trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
